/**
 * What Home has already loaded in this browser session, so coming back to it does not repeat the slow calls
 * (PORTAL-72). Planned maintenance only changes when the nightly extract does, and the contract list barely
 * changes at all, so neither is fetched again on a return visit. Reactive calls can change at any time and are
 * always refreshed.
 *
 * KEYED BY THE PRONTO TOKEN AS WELL AS THE CUSTOMER. A new sign-in has a new token, so another person (or the
 * same person after signing out) never inherits what an earlier session loaded, even though this lives in
 * memory for the life of the page. Nothing here is persisted: a reload starts clean.
 */

interface PlannedLoaded {
  key: string;

  // When the data was last copied from the Extract (ISO, UTC), or null where the list came from the live call
  // and so has no such time. A null here means "keep it until sign-out": there is nothing cheap to compare.
  asOf: string | null;

  // True where only the first page was read, so the list must not be counted in the browser.
  partial: boolean;
}

let plannedLoaded: PlannedLoaded | null = null;

const keyFor = (token: string, customerId: string) => `${token}|${customerId}`;

export const markPlannedLoaded = (token: string, customerId: string, asOf: string | null, partial: boolean = false) => {
  plannedLoaded = { key: keyFor(token, customerId), asOf, partial };
};

export const plannedLoadedFor = (token: string, customerId: string): PlannedLoaded | null => {
  return plannedLoaded !== null && plannedLoaded.key === keyFor(token, customerId) ? plannedLoaded : null;
};

export const clearHomeSession = () => {
  plannedLoaded = null;
};

/** "13 Oct, 9:30 am" in the viewer's own time zone, or '' where there is nothing to show. */
export const formatDataAsOf = (asOf: string | null | undefined): string => {
  if (!asOf) {
    return '';
  }

  const when = new Date(asOf);

  if (Number.isNaN(when.getTime())) {
    return '';
  }

  return when.toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};
